import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAuth } from "hooks/useAuth";
import { useMedicationSafety } from "hooks/useMedicationSafety";

// Alert colour per grade. "avoid" is the label's own wording (contraindicated, do not use,
// avoid); the others are softer on purpose — a label naming something in its warnings is a
// reason to ask, not proof of harm.
const ALERT_SEVERITY = { avoid: "error", warning: "warning", caution: "warning" };

// Keys under safety.interaction, by grade.
const INTERACTION_SUMMARY = {
  avoid: "safety.interaction.avoid",
  warning: "safety.interaction.warning",
  caution: "safety.interaction.caution",
};

// One or two sentences is enough to show why; the full label is a link away.
const MENTIONS_SHOWN = 2;

const LabelLink = ({ href }) => {
  const { t } = useTranslation("medications");
  return href ? (
    <>
      {" "}
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {t("safety.fullLabel")}
      </Link>
    </>
  ) : null;
};

// The quoted sentence is the label's own (English) text, so only the lead-in is translated.
const Quote = ({ who, text, href }) => {
  const { t } = useTranslation("medications");
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
      <strong>{t("safety.labelOf", { who })}</strong> “{text}”
      <LabelLink href={href} />
    </Typography>
  );
};

const Group = ({ title, children }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
      {title}
    </Typography>
    <Stack spacing={1.5}>{children}</Stack>
  </Box>
);

/** Why an allergy conflict was raised, in the patient's terms. */
function allergyReasonText({ medication, allergenName }, reason, t) {
  const med = medication.name;
  const matched = reason.matched;
  switch (reason.kind) {
    case "ingredient":
      return reason.matchedKind === "substance"
        ? t("safety.allergy.ingredient", { med, matched })
        : t("safety.allergy.family", { med, matched });
    case "family":
      return t("safety.allergy.sameClass", { med, allergen: allergenName, matched });
    case "medrt":
      return t("safety.allergy.medrt", { med, matched });
    default:
      return null;
  }
}

function AllergyAlert({ conflict }) {
  const { t } = useTranslation("medications");
  const lead = conflict.reasons.find((r) => r.kind !== "label");
  const quotes = conflict.reasons.filter((r) => r.kind === "label").slice(0, MENTIONS_SHOWN);
  return (
    <Alert severity={ALERT_SEVERITY[conflict.severity]}>
      <AlertTitle>
        {t("safety.allergy.title", {
          med: conflict.medication.name,
          allergen: conflict.allergenName,
        })}
      </AlertTitle>
      {lead && (
        <Typography variant="body2" sx={{ mb: quotes.length ? 1 : 0 }}>
          {allergyReasonText(conflict, lead, t)}
        </Typography>
      )}
      {quotes.map((q) => (
        <Quote key={q.excerpt} who={conflict.medication.name} text={q.excerpt} href={conflict.labelUrl} />
      ))}
    </Alert>
  );
}

function ConditionAlert({ conflict }) {
  const { t } = useTranslation("medications");
  const med = conflict.medication.name;
  const disease = conflict.disease.toLowerCase();
  return (
    <Alert severity={ALERT_SEVERITY[conflict.severity]}>
      <AlertTitle>
        {med} + {conflict.conditionName}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: conflict.excerpt ? 1 : 0 }}>
        {conflict.excerpt
          ? conflict.severity === "avoid"
            ? t("safety.condition.avoid", { med, disease })
            : t("safety.condition.warning", { med, disease })
          : t("safety.condition.medrt", { med, disease })}
      </Typography>
      {conflict.excerpt && <Quote who={med} text={conflict.excerpt} href={conflict.labelUrl} />}
    </Alert>
  );
}

function InteractionAlert({ pair }) {
  const { t } = useTranslation("medications");
  return (
    <Alert severity={ALERT_SEVERITY[pair.severity]}>
      <AlertTitle>
        {pair.medications[0].name} + {pair.medications[1].name}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t(INTERACTION_SUMMARY[pair.severity])}
      </Typography>
      {pair.mentions.slice(0, MENTIONS_SHOWN).map((m) => (
        <Quote key={`${m.fromName}|${m.excerpt}`} who={m.fromName} text={m.excerpt} href={m.labelUrl} />
      ))}
    </Alert>
  );
}

/**
 * "Possible issues" at the foot of the medications page: what the FDA labels of the patient's
 * current medications say about each other, about the allergies on their record, and about
 * the conditions on their record — each with the label's own sentence where there is one.
 */
const MedicationSafetyWarnings = ({ medications }) => {
  const { t } = useTranslation("medications");
  const { userData } = useAuth();
  const allergies = userData?.healthRecords?.allergies;
  const conditions = userData?.healthRecords?.pmh;
  const {
    interactions,
    allergyConflicts,
    conditionConflicts,
    checkedCount,
    uncheckedCount,
    loading,
  } = useMedicationSafety(medications, allergies, conditions);

  if (!(medications || []).length) return null;

  const nothingFound =
    !interactions.length && !allergyConflicts.length && !conditionConflicts.length;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("safety.title")}
      </Typography>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Stack spacing={3}>
          {allergyConflicts.length > 0 && (
            <Group title={t("safety.withAllergies")}>
              {allergyConflicts.map((c) => (
                <AllergyAlert key={c.id} conflict={c} />
              ))}
            </Group>
          )}

          {conditionConflicts.length > 0 && (
            <Group title={t("safety.withConditions")}>
              {conditionConflicts.map((c) => (
                <ConditionAlert key={c.id} conflict={c} />
              ))}
            </Group>
          )}

          {interactions.length > 0 && (
            <Group title={t("safety.betweenMedications")}>
              {interactions.map((pair) => (
                <InteractionAlert key={pair.id} pair={pair} />
              ))}
            </Group>
          )}

          {nothingFound && checkedCount > 0 && (
            <Alert severity="info">
              {t("safety.nothingFound")}
            </Alert>
          )}

          <Box>
            {uncheckedCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("safety.unchecked", { count: uncheckedCount })}{" "}
                {t("safety.uncheckedFix")}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {t("safety.disclaimer")}
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default MedicationSafetyWarnings;
